import { useState, useEffect } from 'react';
import useLazyLoad from './useLazyLoad';

// ============================================================
// Cola de concurrencia — módulo-level singleton
// Todas las instancias del hook comparten la misma cola (máx 2 requests simultáneos).
// ============================================================
let _activeCount = 0;
const _queue = [];

function _onExecuteComplete() {
  _activeCount--;
  _processQueue();
}

function _processQueue() {
  while (_activeCount < 2 && _queue.length > 0) {
    const { execute } = _queue.shift();
    _activeCount++;
    execute().finally(_onExecuteComplete);
  }
}

/**
 * Carga imágenes autenticadas (blobs) con lazy loading y cola de concurrencia.
 * Solo dispara el fetch cuando el elemento entra al viewport.
 * Máximo 2 requests concurrentes para evitar 429.
 *
 * @param {Function} fetchFn - Función que retorna Promise<Response> (blob)
 * @param {string|number} id - ID único del ítem (para deduplicar en cola)
 * @returns {Object} Estado del hook
 * @returns {Object} returns.ref - Ref para el elemento DOM
 * @returns {string|null} returns.blobUrl - URL del blob creado, null mientras carga
 * @returns {boolean} returns.loading - true mientras el fetch está en curso
 * @returns {boolean} returns.failed - true si el fetch falló (estado terminal)
 */
function useAuthenticatedImageLoader(fetchFn, id) {
  const [ref, isIntersecting] = useLazyLoad({ rootMargin: '100px' });
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!isIntersecting || blobUrl || failed || loading) return;

    setLoading(true);

    const execute = async () => {
      try {
        const response = await fetchFn();
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
      } catch (err) {
        setFailed(true);
      } finally {
        setLoading(false);
      }
    };

    _queue.push({ id, execute });
    _processQueue();
  }, [isIntersecting]); // eslint-disable-line react-hooks/exhaustive-deps

  // Revocar blob URL al desmontar para evitar memory leaks
  useEffect(() => {
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobUrl]);

  return { ref, blobUrl, loading, failed };
}

export default useAuthenticatedImageLoader;
